import { QueryClient, queryOptions, skipToken, UseQueryOptions } from "@tanstack/react-query";
import { Build, Job } from "~/components/build/Show/lib/types";
import fetcher from "~/components/build/Show/lib/fetcher";
import { setJobQueryData } from "~/components/build/Show/lib/queries/job";
import { useBuildPageStore } from "~/components/build/Show/lib/useBuildPageStore";

export const jobRetriesQueryKey = (buildId: Build["id"], jobId: Job["id"]) => {
  return ["build", buildId, "jobs", jobId, "retries"];
};

export const jobRetriesQueryOptions = (
  build: Build,
  jobId: Job["id"] | undefined | null,
  queryOptionsArgs: Omit<UseQueryOptions<Job[]>, "queryKey" | "queryFn"> = {},
) => {
  const { enabled: callerEnabled, ...restOptions } = queryOptionsArgs;
  const enabled = !!jobId && (callerEnabled ?? true);

  return queryOptions({
    queryKey: jobRetriesQueryKey(build.id, jobId ?? ""),
    queryFn: enabled
      ? ({ client: queryClient, signal }) =>
          fetchJobRetries(build.buildDataBasePath, jobId, signal).then((retries) => {
            // Set jobs in useBuildPageStore
            useBuildPageStore.getState().setJobs(retries);

            // Pre-populate cache for all returned jobs
            retries.forEach((job: Job) => {
              // Set individual job data
              setJobQueryData(build.id, job, queryClient);

              // Set retries data for other jobs in the chain (they share the same retries list)
              if (job.id !== jobId) {
                queryClient.setQueryData(jobRetriesQueryKey(build.id, job.id), retries);
              }
            });

            return retries;
          })
      : skipToken,
    enabled,
    ...restOptions,
  });
};

function fetchJobRetries(
  buildDataBasePath: Build["buildDataBasePath"],
  jobId: Job["id"],
  signal: AbortSignal,
) {
  const url = new URL(`${buildDataBasePath}/jobs/${jobId}/retries`, window.location.origin);

  return fetcher<Job[]>(url, true, { signal });
}
